/**
 * toolruns — unified read + pin against `/api/v2/tools`.
 *
 * Composes the four per-tool run-stores into a single feed. Pinned-first
 * ordering is enforced server-side; the local cache just mirrors the
 * latest response.
 */
import ToolRunsRepository from '@/pages/my-environment/repositories/ToolRunsRepository';
import { trackAnalyticsEvent } from '@/composables/useAnalytics';
import { computed, ref } from 'vue';

const items = ref([]);
const pinnedTools = ref([]);
const isLoading = ref(false);

export async function loadToolruns({ category = null, limit = 100 } = {}) {
  isLoading.value = true;
  try {
    const response = await ToolRunsRepository.loadRuns({ category, limit });
    items.value = response?.data?.items || [];
    pinnedTools.value = response?.data?.pinned_tools || [];
  } catch (error) {
    items.value = [];
    pinnedTools.value = [];
  } finally {
    isLoading.value = false;
  }
  return items.value;
}

export async function pinTool(toolKey) {
  const response = await ToolRunsRepository.pin(toolKey);
  if (response?.data?.pinned_tools) pinnedTools.value = response.data.pinned_tools;
  trackAnalyticsEvent('environment.tool_pinned', {
    resource_type: 'tool',
    resource_id: toolKey,
    tool_id: toolKey,
    properties: {
      tool_id: toolKey,
      pinned: true,
    },
  });
  return response?.data;
}

export async function unpinTool(toolKey) {
  const response = await ToolRunsRepository.unpin(toolKey);
  if (response?.data?.pinned_tools) pinnedTools.value = response.data.pinned_tools;
  trackAnalyticsEvent('environment.tool_pinned', {
    resource_type: 'tool',
    resource_id: toolKey,
    tool_id: toolKey,
    properties: {
      tool_id: toolKey,
      pinned: false,
    },
  });
  return response?.data;
}

export function useToolruns() {
  return {
    toolruns: computed(() => items.value),
    pinnedTools: computed(() => pinnedTools.value),
    isLoading: computed(() => isLoading.value),
    loadToolruns,
    pinTool,
    unpinTool,
  };
}

export default {
  loadToolruns,
  pinTool,
  unpinTool,
  useToolruns,
};
